// src/components/dashboards/charts/DataCompletenessBar.tsx
// Horizontal stacked bar: data completeness percentage per table type.

import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme, { mixToward, seriesGradient } from "./useChartTheme";
import { athenaColors } from "../../../theme/palette";
import { CompletenessData } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";

interface Props {
  data: CompletenessData | null;
  loading: boolean;
}

const DataCompletenessBar: React.FC<Props> = ({ data, loading }) => {
  const { baseAxis, baseTooltip, baseGrid, textPrimary, theme, isMobile, dark } = useChartTheme();
  const { t } = useTranslation();

  if (loading || !data) {
    return <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />;
  }

  const tables = data.tables;
  if (!tables || tables.length === 0) {
    return (
      <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.analytics.no_completeness_data", "No completeness data available")}
        </Typography>
      </Box>
    );
  }

  // Sort by completeness ascending (highest at top in horizontal bar)
  const sorted = [...tables].sort((a, b) => a.completeness - b.completeness);

  const option = {
    title: {
      text: t("dashboard.analytics.data_completeness", "How Complete Is Your Data?"),
      subtext: t("dashboard.analytics.data_completeness_sub", "% of fields filled per table"),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      subtextStyle: { color: baseAxis.axisLabel.color, fontSize: 11 },
      left: 0,
    },
    tooltip: {
      ...baseTooltip,
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params: any) => {
        const d = params[0];
        const row = sorted[d.dataIndex];
        return `${d.name}: <strong>${d.value}%</strong> (${row?.records ?? 0} records)`;
      },
    },
    grid: { ...baseGrid, left: isMobile ? "35%" : "30%", top: "18%" },
    xAxis: { ...baseAxis, type: "value", max: 100, axisLabel: { ...baseAxis.axisLabel, formatter: "{value}%" } },
    yAxis: {
      ...baseAxis,
      type: "category",
      data: sorted.map((t) => t.table),
      axisLabel: { ...baseAxis.axisLabel, width: 120, overflow: "truncate" },
    },
    series: [
      {
        type: "bar",
        // On the dark ground the traffic light became thirteen red bars out of
        // sixteen: a wall of alarm for what is a ranked distribution, and red
        // is not what "Procedures is 12% filled" means. One brand hue,
        // lightened with the value, keeps the ordering (the bars are sorted
        // and every one carries its own %) without the false alarm. The pale
        // dashboard keeps the traffic light it has today.
        data: sorted.map((t) => ({
          value: t.completeness,
          itemStyle: {
            color: dark
              ? seriesGradient(
                  mixToward(
                    athenaColors.royalBlue,
                    athenaColors.periwinkle,
                    Math.min(1, Math.max(0, t.completeness / 100)),
                  ),
                  "horizontal",
                )
              : seriesGradient(
                  t.completeness > 75
                    ? theme.palette.success.main
                    : t.completeness > 40
                    ? theme.palette.warning.main
                    : theme.palette.error.main,
                  "horizontal",
                ),
          },
        })),
        barMaxWidth: 20,
        itemStyle: { borderRadius: [0, 4, 4, 0] },
        label: {
          show: true,
          position: "right",
          formatter: "{c}%",
          color: textPrimary,
          fontSize: 10,
        },
      },
    ],
  };

  const minH = isMobile ? 220 : 280;
  const barSpacing = isMobile ? 26 : 32;

  return (
    <Box sx={{ width: "100%", height: Math.max(minH, sorted.length * barSpacing + 80) }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default DataCompletenessBar;
